import classNames from 'classnames'
import React, { useRef, useId, useState, useEffect, useCallback } from 'react'

import { RadioButtonV2, RadioButtonV2Props } from '../radioButtonV2'

import styles from './radioButtonGroup.module.scss'

export type RadioOption = Pick<RadioButtonV2Props, 'value' | 'label' | 'disabled'>

export interface RadioButtonGroupProps {
  name: string
  options: RadioOption[]
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  disabled?: boolean
  className?: string
  groupLabel?: string
  id?: string
  itemsClassName?: string
  radioItemClassName?: string
  radioLabelClassName?: string
}

export const RadioButtonGroup = ({
  name,
  options,
  value,
  defaultValue,
  onChange,
  disabled = false,
  className,
  groupLabel,
  id,
  itemsClassName,
  radioItemClassName,
  radioLabelClassName,
}: RadioButtonGroupProps) => {
  const radioRefs = useRef<(HTMLInputElement | null)[]>([])
  const generatedId = useId()
  const groupId = id ?? generatedId

  // Internal state for uncontrolled component mode
  const [selectedRadioValue, setSelectedRadioValue] = useState<string | undefined>(defaultValue)

  const isControlled = value !== undefined
  const selectedValue = isControlled ? value : selectedRadioValue

  // Update internal state when defaultValue changes
  useEffect(() => {
    if (!isControlled && defaultValue !== undefined) {
      setSelectedRadioValue(defaultValue)
    }
  }, [isControlled, defaultValue])

  const handleChange = useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setSelectedRadioValue(newValue)
      }
      onChange?.(newValue)
    },
    [isControlled, onChange],
  )

  const getChangeHandler = useCallback(
    (optionValue: string) => () => {
      handleChange(optionValue)
    },
    [handleChange],
  )

  return (
    <div
      className={classNames(styles.radioButtonGroup, className)}
      role="radiogroup"
      aria-labelledby={`${groupId}-label`}
    >
      {groupLabel && (
        <div id={`${groupId}-label`} className={styles['radio-group-label']}>
          {groupLabel}
        </div>
      )}

      <div className={classNames(styles['radio-group-items'], itemsClassName)}>
        {options.map((option, index) => {
          const isSelected = selectedValue === option.value
          return (
            <RadioButtonV2
              key={option.value}
              id={`${name}-${option.value}`}
              ref={el => {
                radioRefs.current[index] = el
              }}
              name={name}
              value={option.value}
              label={option.label}
              checked={isSelected}
              onChange={getChangeHandler(option.value)}
              disabled={disabled || option.disabled}
              className={radioItemClassName}
              labelClassName={radioLabelClassName}
            />
          )
        })}
      </div>
    </div>
  )
}
